import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import CommonHeader from './CommonHeader';
import CommonFooter from './CommonFooter';
import Loader from './Loader';
import EmptyState from './EmptyState';
import leadersService, { Leader } from '../services/leadersService';
import staticDataService, { StaticData } from '../services/staticDataService';
import bannerService, { BannerData } from '../services/bannerService';
import ceoService, { CeoData } from '../services/ceoService';
import chairmanService, { ChairmanData } from '../services/chairmanService';
import applicationService, { ApplicationData } from '../services/applicationService';
import jwilUpdatesService from '../services/jwilUpdatesService';
import companyPoliciesService from '../services/companyPoliciesService';
import eLibraryService from '../services/eLibraryService';
import categoryService from '../services/categoryService';
import snippetsService from '../services/snippetsService';
import snippetAlbumService from '../services/snippetAlbumService';
import eventsService from '../services/eventsService';
import rewardRecognitionService, { RewardCategory, RewardRecognitionWithDetails } from '../services/rewardRecognitionService';
import { JwilUpdate, CompanyPolicy, ELibrary, Category, Snippet, SnippetAlbum, SnippetMedia, AlbumWithMedia } from '../types/contentManagement';
import type { Event } from '../types/contentManagement';
import ImageCarousel from './ImageCarousel';
import NewsletterDisplay from './NewsletterDisplay';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import './HomePage.css';
import './RewardRecognitionStyles.css';
import './OurLeaders.css';
import authService from '../services/authService';
import usersService, { User } from '../services/usersService';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  
  // Get user data from localStorage
  const [currentUser, setCurrentUser] = useState<any>(null);
  
  // Employee carousel data
  const [newJoinees, setNewJoinees] = useState<User[]>([]);
  const [birthdayUsers, setBirthdayUsers] = useState<User[]>([]);
  const [anniversaryUsers, setAnniversaryUsers] = useState<User[]>([]);
  
  // Celebration confetti state
  const [showConfetti, setShowConfetti] = useState(false);
  const [celebrationMessage, setCelebrationMessage] = useState('');
  
  useEffect(() => {
    const user = authService.getStoredUser();
    setCurrentUser(user);
    loadUserRole();
  }, []);

  const loadUserRole = async () => {
    try {
      const token = localStorage.getItem('jwtToken');
       const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/auth/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const roleName = data.user?.roleName || data.user?.role_name || '';
        console.log('User role loaded:', roleName);
        setUserRole(roleName);
      }
    } catch (error) {
      console.error('Error loading user role:', error);
    }
  };
  
  // Function to get greeting based on current time
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  // Handle logout functionality
  const handleLogout = () => {
    // Clear authentication data
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('userToken');
    localStorage.removeItem('userRole');
    
    // Close dropdown
    setShowProfileDropdown(false);
    
    // Navigate to login page
    window.location.href = '/';
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.user-avatar')) {
        setShowProfileDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  const [showCarouselModal, setShowCarouselModal] = useState(false);
  const [sliderKey, setSliderKey] = useState(0);
  const [currentDate, setCurrentDate] = useState(new Date()); // Always starts with current month
  const [leaveDates, setLeaveDates] = useState<number[]>([]);
  
  // Awards data structure matching the reference design
  const [awardsData, setAwardsData] = useState([
    { id: 1, name: 'John Smith', image: '/assets/images/prasenjit-mukherjee.jpg', award: 'PRIDEOF JWIL', position: { top: '55%', left: '8%' }, badgePosition: { top: '45%', left: '12%' } },
    { id: 2, name: 'Sarah Johnson', image: '/assets/images/manoj-kumar-mishra.jpg', award: 'DIVERSITY CHAMPION', position: { top: '35%', left: '22%' }, badgePosition: { top: '40%', left: '26%' } },
    { id: 3, name: 'Mike Wilson', image: '/assets/images/prasenjit-mukherjee.jpg', award: 'STAR CONTRIBUTOR', position: { top: '15%', left: '45%' }, badgePosition: { top: '25%', left: '49%' } },
    { id: 4, name: 'Emily Davis', image: '/assets/images/manoj-kumar-mishra.jpg', award: '#CONTRIBUTOR', position: { top: '8%', left: '65%' }, badgePosition: { top: '18%', left: '69%' } },
    { id: 5, name: 'Robert Brown', image: '/assets/images/prasenjit-mukherjee.jpg', award: 'THE HIGHFLYER AWARD', position: { top: '8%', left: '82%' }, badgePosition: { top: '18%', left: '86%' } },
    { id: 6, name: 'Lisa Anderson', image: '/assets/images/manoj-kumar-mishra.jpg', award: 'CEO GOLD CLUB', position: { top: '45%', left: '88%' }, badgePosition: { top: '55%', left: '92%' } },
    { id: 7, name: 'David Miller', image: '/assets/images/prasenjit-mukherjee.jpg', award: 'THE SHEROES AWARD', position: { top: '75%', left: '25%' }, badgePosition: { top: '85%', left: '29%' } },
    { id: 8, name: 'Jennifer Wilson', image: '/assets/images/manoj-kumar-mishra.jpg', award: 'THE OPERA AWARD', position: { top: '75%', left: '45%' }, badgePosition: { top: '85%', left: '49%' } },
    { id: 9, name: 'James Taylor', image: '/assets/images/prasenjit-mukherjee.jpg', award: 'YOUNG ACHIEVER AWARD', position: { top: '75%', left: '65%' }, badgePosition: { top: '85%', left: '69%' } }
  ]);
  const [showCeoDropdown, setShowCeoDropdown] = useState(false);
  const [showChairmanDropdown, setShowChairmanDropdown] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [activeTab, setActiveTab] = useState('JWIL Latest Updates');
  const [userRole, setUserRole] = useState(''); // Loaded from auth API
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showDharmaModal, setShowDharmaModal] = useState(false);
  const [showTenetsModal, setShowTenetsModal] = useState(false);
  
  // Company Policies data structure
  const [companyPolicies] = useState([
    {
      id: 1,
      title: 'Employee Handbook',
      category: 'HR',
      type: 'document',
      documentUrl: '/assets/documents/employee-handbook.pdf',
      icon: '/assets/images/doc-icon.svg',
      uploadDate: 'Dec 15, 2024'
    },
    {
      id: 2,
      title: 'Code of Conduct',
      category: 'General',
      type: 'document',
      documentUrl: '/assets/documents/code-of-conduct.pdf',
      icon: '/assets/images/doc-icon.svg',
      uploadDate: 'Dec 10, 2024'
    },
    {
      id: 3,
      title: 'Leave Policy',
      category: 'HR',
      type: 'document',
      documentUrl: '/assets/documents/leave-policy.pdf',
      icon: '/assets/images/doc-icon.svg',
      uploadDate: 'Dec 08, 2024'
    },
    {
      id: 4,
      title: 'IT Security Guidelines',
      category: 'IT',
      type: 'link',
      link: 'https://jwil.com/security-guidelines',
      icon: '/assets/images/link-icon.svg',
      uploadDate: 'Dec 05, 2024'
    },
    {
      id: 5,
      title: 'Travel & Expense Policy',
      category: 'Finance',
      type: 'document',
      documentUrl: '/assets/documents/travel-expense-policy.pdf',
      icon: '/assets/images/doc-icon.svg',
      uploadDate: 'Dec 01, 2024'
    },
    {
      id: 6,
      title: 'Health & Safety Guidelines',
      category: 'General',
      type: 'link',
      link: 'https://jwil.com/health-safety',
      icon: '/assets/images/link-icon.svg',
      uploadDate: 'Nov 28, 2024'
    },
    {
      id: 7,
      title: 'Performance Management',
      category: 'HR',
      type: 'document',
      documentUrl: '/assets/documents/performance-management.pdf',
      icon: '/assets/images/doc-icon.svg',
      uploadDate: 'Nov 25, 2024'
    },
    {
      id: 8,
      title: 'Remote Work Policy',
      category: 'General',
      type: 'document',
      documentUrl: '/assets/documents/remote-work-policy.pdf',
      icon: '/assets/images/doc-icon.svg',
      uploadDate: 'Nov 20, 2024'
    },
    {
      id: 9,
      title: 'Data Privacy Policy',
      category: 'IT',
      type: 'document',
      documentUrl: '/assets/documents/data-privacy-policy.pdf',
      icon: '/assets/images/doc-icon.svg',
      uploadDate: 'Nov 15, 2024'
    },
    {
      id: 10,
      title: 'Workplace Harassment Policy',
      category: 'HR',
      type: 'document',
      documentUrl: '/assets/documents/workplace-harassment-policy.pdf',
      icon: '/assets/images/doc-icon.svg',
      uploadDate: 'Nov 10, 2024'
    },
    {
      id: 11,
      title: 'Environmental Policy',
      category: 'General',
      type: 'link',
      link: 'https://jwil.com/environmental-policy',
      icon: '/assets/images/link-icon.svg',
      uploadDate: 'Nov 05, 2024'
    },
    {
      id: 12,
      title: 'Quality Management System',
      category: 'General',
      type: 'document',
      documentUrl: '/assets/documents/quality-management-system.pdf',
      icon: '/assets/images/doc-icon.svg',
      uploadDate: 'Oct 30, 2024'
    }
  ]);
  const [showAllPolicies, setShowAllPolicies] = useState(false);
  const [showAllProcedures, setShowAllProcedures] = useState(false);
  const [showAllUpdates, setShowAllUpdates] = useState(false);
  const [showAllSnippets, setShowAllSnippets] = useState(false);
  const [showAllEvents, setShowAllEvents] = useState(false);
  const [leaders, setLeaders] = useState<Leader[]>([]);
  const [leadersLoading, setLeadersLoading] = useState(true);
  const [leadersSliderKey, setLeadersSliderKey] = useState(0);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<any>(null);
  
  // Content Management State
  const [jwilUpdates, setJwilUpdates] = useState<JwilUpdate[]>([]);
  const [companyPoliciesData, setCompanyPoliciesData] = useState<CompanyPolicy[]>([]);
  const [eLibrariesData, setELibrariesData] = useState<ELibrary[]>([]);
  const [snippetsData, setSnippetsData] = useState<Snippet[]>([]);
  const [snippetAlbums, setSnippetAlbums] = useState<SnippetAlbum[]>([]);
  const [othersMedia, setOthersMedia] = useState<SnippetMedia[]>([]);
  const [selectedAlbumMedia, setSelectedAlbumMedia] = useState<AlbumWithMedia | null>(null);
  const [showAlbumModal, setShowAlbumModal] = useState(false);
  const [eventsData, setEventsData] = useState<Event[]>([]);
  const [contentLoading, setContentLoading] = useState(true);
  const [imageRefreshKey, setImageRefreshKey] = useState(Date.now());
  
  // JWIL Updates mock data (fallback)
  const [jwilUpdatesFallback] = useState([
    {
      title: 'Jal Jeevan Mission Contribution',
      description: 'JWIL is proud to contribute to ensuring every rural household has access to clean water',
      date: 'Dec 15, 2024'
    },
    {
      title: 'Diwali Celebration',
      description: 'JWIL celebrated Diwali with great enthusiasm and joy across all offices',
      date: 'Dec 12, 2024'
    },
    {
      title: 'Employee Portal Launch',
      description: 'New employee portal launched with enhanced features and user experience',
      date: 'Dec 10, 2024'
    },
    {
      title: 'New Project Announcement',
      description: 'JWIL announces new water treatment project in Maharashtra',
      date: 'Dec 08, 2024'
    },
    {
      title: 'Q3 Results Published',
      description: 'JWIL reports strong Q3 performance with 25% growth in revenue',
      date: 'Dec 05, 2024'
    },
    {
      title: 'Team Building Event',
      description: 'Annual team building event conducted successfully at Goa office',
      date: 'Dec 01, 2024'
    },
    {
      title: 'New Office Opening',
      description: 'JWIL opens new regional office in Bangalore to expand operations',
      date: 'Nov 28, 2024'
    },
    {
      title: 'Sustainability Initiative',
      description: 'JWIL launches new sustainability initiative to reduce carbon footprint',
      date: 'Nov 25, 2024'
    },
    {
      title: 'Training Program Launch',
      description: 'New employee training program launched for skill development',
      date: 'Nov 20, 2024'
    },
    {
      title: 'Award Recognition',
      description: 'JWIL receives Best Water Management Company award for 2024',
      date: 'Nov 15, 2024'
    }
  ]);
  
  // Snippets mock data (fallback)
  const [snippetsFallback] = useState([
    {
      id: 1,
      title: "Company Annual Report 2024",
      description: "Comprehensive overview of our achievements and financial performance this year.",
      image: "/assets/images/update-img.png",
      type: "image",
      link: "/assets/images/banner-image.jpg",
      date: "Dec 15, 2024"
    },
    {
      id: 2,
      title: "CEO Leadership Training Video",
      description: "Exclusive training session on leadership principles and team management.",
      image: "/assets/images/manoj-kumar-mishra.jpg", 
      type: "video",
      link: "https://www.youtube.com/embed/dQw4w9WgXcQ",
      date: "Dec 10, 2024"
    },
    {
      id: 3,
      title: "Employee Handbook 2024",
      description: "Updated policies, procedures, and guidelines for all employees.",
      image: "/assets/images/doc-icon.svg",
      type: "ebook",
      link: "https://example.com/employee-handbook-2024.pdf",
      date: "Dec 5, 2024"
    },
    {
      id: 4,
      title: "Team Building Workshop Photos",
      description: "Memorable moments from our recent team building activities and workshops.",
      image: "/assets/images/prasenjit-mukherjee.jpg",
      type: "image", 
      link: "/assets/images/banner-image.jpg",
      date: "Nov 28, 2024"
    },
    {
      id: 5,
      title: "Product Demo Presentation",
      description: "Latest product features and demonstrations for our new software release.",
      image: "/assets/images/rocket-lunch.gif",
      type: "video",
      link: "https://www.youtube.com/embed/ScMzIvxBSi4",
      date: "Nov 20, 2024"
    },
    {
      id: 6,
      title: "Financial Planning Guide",
      description: "Comprehensive guide for personal and professional financial planning strategies.",
      image: "/assets/images/preseverance.png",
      type: "ebook",
      link: "https://example.com/financial-planning-guide.pdf", 
      date: "Nov 15, 2024"
    },
    {
      id: 7,
      title: "Office Renovation Progress",
      description: "Before and after photos of our office renovation project completion.",
      image: "/assets/images/rishabh-sethi.jpg",
      type: "image",
      link: "/assets/images/banner-image.jpg",
      date: "Nov 10, 2024"
    },
    {
      id: 8,
      title: "Quarterly Review Meeting",
      description: "Recording of our Q4 quarterly review meeting and performance discussions.",
      image: "/assets/images/flag.gif",
      type: "video",
      link: "https://www.youtube.com/embed/jNQXAC9IVRw",
      date: "Nov 5, 2024"
    }
  ]);

  // Sample data for events
  const [events] = useState([
    {
      id: 1,
      title: "Annual Company Retreat 2024",
      description: "Join us for our annual company retreat featuring team building activities, strategic planning sessions, and networking opportunities.",
      entryFee: "Free for employees",
      eventDate: "Jan 15, 2025",
      eventTime: "9:00 AM - 6:00 PM",
      link: "https://example.com/company-retreat-2024",
      image: "/assets/images/banner-image.jpg"
    },
    {
      id: 2,
      title: "Tech Innovation Workshop",
      description: "Explore the latest technologies and innovation trends that will shape our industry in the coming years.",
      entryFee: "$50",
      eventDate: "Jan 22, 2025", 
      eventTime: "2:00 PM - 5:00 PM",
      link: "https://example.com/tech-workshop-2025",
      image: "/assets/images/rocket-lunch.gif"
    },
    {
      id: 3,
      title: "Leadership Development Seminar",
      description: "Enhance your leadership skills with expert speakers and interactive workshops designed for managers and team leads.",
      entryFee: null,
      eventDate: "Feb 5, 2025",
      eventTime: "10:00 AM - 4:00 PM", 
      link: "https://example.com/leadership-seminar-2025",
      image: "/assets/images/manoj-kumar-mishra.jpg"
    },
    {
      id: 4,
      title: "Quarterly All-Hands Meeting",
      description: "Company-wide meeting to discuss Q1 results, upcoming projects, and strategic initiatives for the next quarter.",
      entryFee: null,
      eventDate: "Feb 12, 2025",
      eventTime: "3:00 PM - 4:30 PM",
      link: "https://example.com/all-hands-q1-2025",
      image: "/assets/images/astronaut-flag.gif"
    },
    {
      id: 5,
      title: "Employee Wellness Fair",
      description: "Discover wellness resources, health screenings, fitness activities, and mental health support services.",
      entryFee: "Free",
      eventDate: "Feb 20, 2025",
      eventTime: "11:00 AM - 3:00 PM",
      link: "https://example.com/wellness-fair-2025",
      image: "/assets/images/preseverance.png"
    },
    {
      id: 6,
      title: "Product Launch Celebration",
      description: "Celebrate the launch of our newest product with demos, presentations, and networking with the development team.",
      entryFee: "Free for employees",
      eventDate: "Mar 1, 2025",
      eventTime: "6:00 PM - 9:00 PM",
      link: "https://example.com/product-launch-2025",
      image: "/assets/images/flag.gif"
    }
  ]);
  
  // Debug state changes
  useEffect(() => {
    console.log('showCeoDropdown changed to:', showCeoDropdown);
  }, [showCeoDropdown]);
  
  useEffect(() => {
    console.log('showSearchDropdown changed to:', showSearchDropdown);
  }, [showSearchDropdown]);

  // Force slider reinitialization on component mount
  useEffect(() => {
    const timer = setTimeout(() => {
      setSliderKey(prev => prev + 1);
    }, 100);
    return () => clearTimeout(timer);
  }, []);
  const [bannerData, setBannerData] = useState<BannerData | null>(null);
  const [ceoData, setCeoData] = useState<CeoData | null>(null);
  const [chairmanData, setChairmanData] = useState<ChairmanData | null>(null);
  const [applicationData, setApplicationData] = useState<ApplicationData[]>([]);
  const [loading, setLoading] = useState(true);

  // Reward Recognition State
  const [rewardCategories, setRewardCategories] = useState<RewardCategory[]>([]);
  const [rewardRecognitions, setRewardRecognitions] = useState<RewardRecognitionWithDetails[]>([]);
  const [expandedCategory, setExpandedCategory] = useState<number | null>(null);
  
  // Policy and Procedure Categories State
  const [policyCategories, setPolicyCategories] = useState<Category[]>([]);
  const [libraryCategories, setLibraryCategories] = useState<Category[]>([]);
  const [expandedPolicyCategory, setExpandedPolicyCategory] = useState<number | null>(null);
  const [expandedLibraryCategory, setExpandedLibraryCategory] = useState<number | null>(null);

  // Set first category as expanded by default
  useEffect(() => {
    if (rewardCategories.length > 0 && expandedCategory === null) {
      setExpandedCategory(rewardCategories[0].id);
    }
  }, [rewardCategories]);

  useEffect(() => {
    if (policyCategories.length > 0 && expandedPolicyCategory === null) {
      setExpandedPolicyCategory(policyCategories[0].id);
    }
  }, [policyCategories]);

  useEffect(() => {
    if (libraryCategories.length > 0 && expandedLibraryCategory === null) {
      setExpandedLibraryCategory(libraryCategories[0].id);
    }
  }, [libraryCategories]);


  const handleSearchFocus = () => {
    console.log('Search focused, showing dropdown');
    setShowSearchDropdown(true);
  };

  const handleSearchBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    console.log('Search blurred, hiding dropdown');
    // Don't hide if clicking on dropdown items
    if (e.relatedTarget && (e.relatedTarget as HTMLElement).closest('.search-results-dropdown')) {
      return;
    }
    setTimeout(() => setShowSearchDropdown(false), 200);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('Search changed:', e.target.value);
    setSearchQuery(e.target.value);
    setShowSearchDropdown(true);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setShowSearchDropdown(false);
  };

  // Calendar helper functions
  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const getMonthName = (date: Date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prevDate => {
      const newDate = new Date(prevDate);
      if (direction === 'prev') {
        newDate.setMonth(newDate.getMonth() - 1);
      } else {
        newDate.setMonth(newDate.getMonth() + 1);
      }
      return newDate;
    });
  };

  // Handle tab change with slider reinitialization
  const handleTabChange = (tabName: string) => {
    setActiveTab(tabName);
    setShowMobileMenu(false);
    
    // Destroy and reinitialize leaders slider
    setLeadersSliderKey(0);
    
    // Use setTimeout to ensure tab content is rendered before reinitializing sliders
    setTimeout(() => {
      // Reinitialize leaders slider with new key
      setLeadersSliderKey(Date.now());
      
      // Trigger window resize event to make react-slick recalculate responsive breakpoints
      window.dispatchEvent(new Event('resize'));
      
      // Additional resize event after a short delay for better reliability
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
        
        // Also refresh jQuery-based slick sliders if they exist
        if (typeof (window as any).$ !== 'undefined') {
          const sliders = document.querySelectorAll('.slick-initialized');
          sliders.forEach((slider) => {
            try {
              const $slider = (window as any).$(slider);
              if ($slider.slick && typeof $slider.slick === 'function') {
                $slider.slick('setPosition');
              }
            } catch (error) {
              // Silently handle errors
            }
          });
        }
      }, 100);
    }, 100);
  };

  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const today = new Date();
    const isCurrentMonth = currentDate.getMonth() === today.getMonth() && currentDate.getFullYear() === today.getFullYear();
    const todayDate = today.getDate();

    const calendarGrid = [];
    
    // Create a complete 6-week grid (42 cells total)
    const totalCells = 42;
    let dayCounter = 1;
    
    for (let i = 0; i < totalCells; i++) {
      if (i < 7) {
        // First row: day names
        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        calendarGrid.push(
          <div key={`dayname-${i}`} className="day-name">
            {dayNames[i]}
          </div>
        );
      } else {
        // Calculate position in the calendar grid (excluding header row)
        const gridPosition = i - 7;
        
        if (gridPosition < firstDay || dayCounter > daysInMonth) {
          // Empty cell
          calendarGrid.push(<div key={`empty-${i}`} className="empty-day"></div>);
        } else {
          // Day cell
          let className = 'day';
          
          if (isCurrentMonth && dayCounter === todayDate) {
            className += ' today';
          }
          
          if (leaveDates.includes(dayCounter)) {
            className += ' absent';
          }

          calendarGrid.push(
            <div key={`day-${dayCounter}`} className={className}>
              {dayCounter}
            </div>
          );
          dayCounter++;
        }
      }
    }

    return calendarGrid;
  };

  const handleApplicationClick = (link: string) => {
    if (link.startsWith('http')) {
      window.open(link, '_blank');
    } else {
      navigate(link);
    }
  };

  const handlePolicyClick = (policy: any) => {
    if (policy.type === 'document') {
      // Create a temporary link element to trigger download
      const link = document.createElement('a');
      link.href = policy.documentUrl;
      link.download = policy.title + '.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else if (policy.type === 'link') {
      // Open link in new tab
      window.open(policy.link, '_blank');
    }
  };

  const handleSnippetClick = (snippet: Snippet) => {
    if (snippet.category === 'image') {
      setSelectedMedia({ ...snippet, link: snippetsService.getImageUrl(snippet.image) });
      setShowImageModal(true);
    } else if (snippet.category === 'video') {
      setSelectedMedia({ ...snippet, link: snippetsService.getVideoUrl(snippet.video) });
      setShowVideoModal(true);
    } else if (snippet.category === 'ebook') {
      window.open(snippet.ebook_link, '_blank');
    }
  };

  const handleEventClick = (event: any) => {
    window.open(event.link, '_blank');
  };

  const closeImageModal = () => {
    setShowImageModal(false);
    setSelectedMedia(null);
  };

  const closeVideoModal = () => {
    setShowVideoModal(false);
    setSelectedMedia(null);
  };

  useEffect(() => {
    const fetchStaticData = async () => {
      try {
        setLoading(true);
        const [bannerResponse, ceoResponse, chairmanResponse, applicationsResponse] = await Promise.all([
          bannerService.getBanner(),
          ceoService.getCeo(),
          chairmanService.getChairman(),
          applicationService.getApplications()
        ]);
        setBannerData(bannerResponse.success ? bannerResponse.data : null);
        
        const ceoDataResult = ceoResponse.success ? ceoResponse.data : null;
        console.log('HomePage CEO data loaded:', ceoDataResult); // Debug log
        if (ceoDataResult) {
          console.log('CEO icon path:', ceoDataResult.icon); // Debug log
          console.log('CEO image URL will be:', ceoService.getImageUrl(ceoDataResult.icon)); // Debug log
        }
        setCeoData(ceoDataResult);
        
        const chairmanDataResult = chairmanResponse.success ? chairmanResponse.data : null;
        console.log('HomePage Chairman data loaded:', chairmanDataResult);
        if (chairmanDataResult) {
          console.log('Chairman icon path:', chairmanDataResult.icon);
          console.log('Chairman image URL will be:', chairmanService.getImageUrl(chairmanDataResult.icon));
        }
        setChairmanData(chairmanDataResult);
        
        // Set application data from the new API
        console.log('🏠 HomePage - Applications API response:', applicationsResponse);
        const applications = applicationsResponse.success ? applicationsResponse.data.filter(app => app.is_visible) : [];
        console.log('🏠 HomePage - Filtered visible applications:', applications.length, 'applications');
        setApplicationData(applications);
      } catch (error) {
        console.error('Error fetching static data:', error);
      } finally {
        setLoading(false);
      }
    };

    const loadLeaders = async () => {
      try {
        setLeadersLoading(true);
        const response = await leadersService.getVisible();
        if (response.success) {
          setLeaders(response.data);
        }
      } catch (error) {
        console.error('Error loading leaders:', error);
      } finally {
        setLeadersLoading(false);
      }
    };

    const fetchContentData = async () => {
      try {
        setContentLoading(true);
        const [updates, policies, libraries, policyCats, libraryCats, snippets, albums, othersMedia, events, categories, recognitions] = await Promise.all([
          jwilUpdatesService.getVisibleUpdates().catch(err => { console.error('JWIL Updates error:', err); return []; }),
          companyPoliciesService.getVisiblePolicies().catch(err => { console.error('Policies error:', err); return []; }),
          eLibraryService.getVisibleLibraries().catch((err: any) => { console.error('E-Library error:', err); return []; }),
          categoryService.getCategoriesByType('policy').catch(err => { console.error('Policy categories error:', err); return []; }),
          categoryService.getCategoriesByType('library').catch(err => { console.error('Library categories error:', err); return []; }),
          snippetsService.getVisibleSnippets().catch(err => { console.error('Snippets error:', err); return []; }),
          snippetAlbumService.getVisibleAlbums().catch(err => { console.error('Snippet Albums error:', err); return []; }),
          snippetAlbumService.getMediaWithoutAlbum(false).catch(err => { console.error('Others Media error:', err); return []; }),
          eventsService.getUpcomingEvents().catch(err => { console.error('Events error:', err); return []; }),
          rewardRecognitionService.getCategories().catch(err => { console.error('Reward categories error:', err); return []; }),
          rewardRecognitionService.getRewardRecognitions().catch(err => { console.error('Reward recognitions error:', err); return []; })
        ]);
        
        console.log('📰 Fetched JWIL Updates:', updates.length);
        console.log('📄 Fetched Company Policies:', policies.length);
        console.log('📚 Fetched E-Library:', libraries.length);
        console.log('🏷️ Fetched Policy Categories:', policyCats.length);
        console.log('🏷️ Fetched Library Categories:', libraryCats.length);
        console.log('📚 Fetched Snippets:', snippets.length);
        console.log('📅 Fetched Events:', events.length);
        console.log('🏆 Fetched Reward Categories:', categories.length);
        console.log('🎖️ Fetched Reward Recognitions:', recognitions.length);
        
        setJwilUpdates(updates);
        setCompanyPoliciesData(policies);
        setELibrariesData(libraries);
        setPolicyCategories(policyCats);
        setLibraryCategories(libraryCats);
        setSnippetsData(snippets);
        setSnippetAlbums(albums);
        setOthersMedia(othersMedia);
        setEventsData(events);
        setRewardCategories(categories);
        setRewardRecognitions(recognitions);
        setImageRefreshKey(Date.now()); // Update refresh key when content is fetched
      } catch (error) {
        console.error('Error fetching content data:', error);
        // Set empty arrays as fallback
        setJwilUpdates([]);
        setCompanyPoliciesData([]);
        setELibrariesData([]);
        setSnippetsData([]);
        setEventsData([]);
      } finally {
        setContentLoading(false);
      }
    };


    const fetchEmployeeData = async () => {
      try {
        const users = await usersService.getAllUsers();
        const today = new Date();
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(today.getDate() - 7);
        
        // Filter New Joinees (joined in last 7 days)
        const newJoineesFiltered = users.filter(user => {
          if (!user.dateOfJoining) return false;
          const joiningDate = new Date(user.dateOfJoining);
          return joiningDate >= sevenDaysAgo && joiningDate <= today;
        });
        
        // Filter Birthday Users (birthday TODAY only)
        const birthdayFiltered = users.filter(user => {
          if (!user.dateOfBirth) return false;
          const birthDate = new Date(user.dateOfBirth);
          const todayMonth = today.getMonth();
          const todayDate = today.getDate();
          const birthMonth = birthDate.getMonth();
          const birthDay = birthDate.getDate();
          
          // Check if birthday is today
          return birthMonth === todayMonth && birthDay === todayDate;
        });
        
        // Filter Work Anniversary Users (anniversary TODAY only, at least 1 year old)
        const anniversaryFiltered = users.filter(user => {
          if (!user.dateOfJoining) return false;
          const joiningDate = new Date(user.dateOfJoining);
          const todayMonth = today.getMonth();
          const todayDate = today.getDate();
          const joinMonth = joiningDate.getMonth();
          const joinDay = joiningDate.getDate();
          const joinYear = joiningDate.getFullYear();
          const currentYear = today.getFullYear();
          
          // Must be at least 1 year old
          if (currentYear - joinYear < 1) return false;
          
          // Check if anniversary is today
          return joinMonth === todayMonth && joinDay === todayDate;
        });
        
        setNewJoinees(newJoineesFiltered);
        setBirthdayUsers(birthdayFiltered);
        setAnniversaryUsers(anniversaryFiltered);
      } catch (error) {
        console.error('Error fetching employee data:', error);
      }
    };

    fetchStaticData();
    fetchContentData();
    loadLeaders();
    fetchEmployeeData();

    // Listen for application updates from EditContent
    const handleApplicationsUpdated = () => {
      console.log('Applications updated, refreshing homepage data...');
      fetchStaticData();
    };

    const handleContentUpdated = () => {
      console.log('Content updated, refreshing content data...');
      fetchContentData();
    };

    window.addEventListener('applicationsUpdated', handleApplicationsUpdated);
    window.addEventListener('contentUpdated', handleContentUpdated);
    
    return () => {
      window.removeEventListener('applicationsUpdated', handleApplicationsUpdated);
      window.removeEventListener('contentUpdated', handleContentUpdated);
    };
  }, []);

  // Helper function to get profile picture URL
  const getProfilePictureUrl = (user: User): string => {
    const API_BASE_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5001';
    if (user.profilePicture) {
      return `${API_BASE_URL}${user.profilePicture}`;
    }
    // Use a placeholder avatar from UI Avatars service
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(user.employeeName)}&background=007bff&color=fff&size=200`;
  };

  // Helper function to calculate years of service
  const getYearsOfService = (joiningDate: string): number => {
    const today = new Date();
    const joined = new Date(joiningDate);
    return today.getFullYear() - joined.getFullYear();
  };

  // Helper function to format date
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' };
    return date.toLocaleDateString('en-GB', options);
  };

  // Helper function to check if birthday is today
  const isBirthdayToday = (dateOfBirth: string): boolean => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    return birthDate.getMonth() === today.getMonth() && birthDate.getDate() === today.getDate();
  };

  // Helper function to check if anniversary is today
  const isAnniversaryToday = (joiningDate: string): boolean => {
    const today = new Date();
    const joined = new Date(joiningDate);
    return joined.getMonth() === today.getMonth() && joined.getDate() === today.getDate();
  };

  // Helper function to send birthday wish email
  const sendBirthdayWish = (user: User) => {
    const subject = encodeURIComponent(`Happy Birthday ${user.employeeName}! 🎂`);
    const body = encodeURIComponent(
      `Dear ${user.employeeName},\n\n` +
      `Wishing you a very Happy Birthday! 🎉🎂\n\n` +
      `May this special day bring you joy, happiness, and wonderful memories. ` +
      `We are grateful to have you as part of our team.\n\n` +
      `Have a fantastic day filled with celebrations!\n\n` +
      `Best wishes,\n` +
      `JWIL Team`
    );
    window.location.href = `mailto:${user.email}?subject=${subject}&body=${body}`;
  };

  // Helper function to send work anniversary wish email
  const sendAnniversaryWish = (user: User) => {
    const years = user.dateOfJoining ? getYearsOfService(user.dateOfJoining) : 1;
    const subject = encodeURIComponent(`Happy ${years} Year${years !== 1 ? 's' : ''} Work Anniversary! 🎊`);
    const body = encodeURIComponent(
      `Dear ${user.employeeName},\n\n` +
      `Congratulations on completing ${years} year${years !== 1 ? 's' : ''} with JWIL! 🎊🎉\n\n` +
      `Your dedication, hard work, and contributions have been invaluable to our organization. ` +
      `Thank you for being an integral part of our team and for your continued commitment.\n\n` +
      `Here's to many more years of success and growth together!\n\n` +
      `Best regards,\n` +
      `JWIL Team`
    );
    window.location.href = `mailto:${user.email}?subject=${subject}&body=${body}`;
  };

  // Removed leave calendar functionality since Leave & Attendance card is replaced with Work Anniversaries

  // Check if current user has celebration today (birthday or work anniversary)
  useEffect(() => {
    const checkUserCelebration = async () => {
      try {
        // Get current user email from stored user
        const storedUser = authService.getStoredUser();
        if (!storedUser || !storedUser.email) return;

        // Check if we already showed confetti today
        const today = new Date().toDateString();
        const lastShownDate = localStorage.getItem('confettiShownDate');
        const lastShownEmail = localStorage.getItem('confettiShownEmail');
        
        if (lastShownDate === today && lastShownEmail === storedUser.email) {
          // Already shown today for this user
          return;
        }

        // Fetch user details from API
        const users = await usersService.getAllUsers();
        const currentUserData = users.find(u => u.email === storedUser.email);
        
        if (!currentUserData) return;

        const now = new Date();
        const currentMonth = now.getMonth();
        const currentDate = now.getDate();
        
        let hasCelebration = false;
        let message = '';
        let hasBirthday = false;
        let hasAnniversary = false;
        let anniversaryYears = 0;

        // Check for birthday
        if (currentUserData.dateOfBirth) {
          const birthDate = new Date(currentUserData.dateOfBirth);
          if (birthDate.getMonth() === currentMonth && birthDate.getDate() === currentDate) {
            hasBirthday = true;
            hasCelebration = true;
          }
        }

        // Check for work anniversary
        if (currentUserData.dateOfJoining) {
          const joiningDate = new Date(currentUserData.dateOfJoining);
          const joinYear = joiningDate.getFullYear();
          const currentYear = now.getFullYear();
          
          if (currentYear - joinYear >= 1 && 
              joiningDate.getMonth() === currentMonth && 
              joiningDate.getDate() === currentDate) {
            hasAnniversary = true;
            hasCelebration = true;
            anniversaryYears = currentYear - joinYear;
          }
        }

        // Create combined message if both events on same day
        if (hasBirthday && hasAnniversary) {
          message = `🎉 Happy Birthday & ${anniversaryYears} Year${anniversaryYears !== 1 ? 's' : ''} Work Anniversary! 🎂🎊`;
        } else if (hasBirthday) {
          message = '🎉 Happy Birthday! 🎂';
        } else if (hasAnniversary) {
          message = `🎊 Happy ${anniversaryYears} Year${anniversaryYears !== 1 ? 's' : ''} Work Anniversary! 🎉`;
        }

        if (hasCelebration) {
          setCelebrationMessage(message);
          setShowConfetti(true);
          
          // Store that we showed confetti today for this user
          localStorage.setItem('confettiShownDate', today);
          localStorage.setItem('confettiShownEmail', storedUser.email);
          
          // Auto-hide confetti after 10 seconds
          setTimeout(() => {
            setShowConfetti(false);
          }, 10000);
        }
      } catch (error) {
        console.error('Error checking user celebration:', error);
      }
    };

    checkUserCelebration();
  }, []);

  return (
    <>
      {/* Loader */}
      {loading && <Loader message="Loading content..." />}
      
      {/* Celebration Confetti */}
      {showConfetti && (
        <>
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            zIndex: 9999,
            overflow: 'hidden'
          }}>
            {/* Confetti Animation */}
            {Array.from({ length: 100 }).map((_, i) => (
              <div
                key={i}
                style={{
                  position: 'absolute',
                  top: '-10px',
                  left: `${Math.random() * 100}%`,
                  width: '10px',
                  height: '10px',
                  backgroundColor: ['#ff6b6b', '#4ecdc4', '#45b7d1', '#f9ca24', '#6c5ce7', '#fd79a8'][Math.floor(Math.random() * 6)],
                  opacity: 0.8,
                  animation: `confettiFall ${3 + Math.random() * 2}s linear ${Math.random() * 2}s infinite`,
                  transform: `rotate(${Math.random() * 360}deg)`
                }}
              />
            ))}
          </div>
          
          {/* Celebration Message */}
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10000,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            padding: '40px 60px',
            borderRadius: '20px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
            textAlign: 'center',
            animation: 'celebrationPulse 1s ease-in-out infinite',
            pointerEvents: 'auto'
          }}>
            <h1 style={{
              fontSize: '48px',
              fontWeight: 'bold',
              color: '#007bff',
              margin: 0,
              textShadow: '2px 2px 4px rgba(0,0,0,0.1)'
            }}>
              {celebrationMessage}
            </h1>
            <button
              onClick={() => setShowConfetti(false)}
              style={{
                marginTop: '20px',
                padding: '10px 30px',
                fontSize: '16px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '25px',
                cursor: 'pointer',
                fontWeight: '600',
                transition: 'all 0.3s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#0056b3'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#007bff'}
            >
              Thank You! 😊
            </button>
          </div>
          
          {/* Add CSS animations */}
          <style>{`
            @keyframes confettiFall {
              to {
                transform: translateY(100vh) rotate(720deg);
              }
            }
            
            @keyframes celebrationPulse {
              0%, 100% {
                transform: translate(-50%, -50%) scale(1);
              }
              50% {
                transform: translate(-50%, -50%) scale(1.05);
              }
            }
          `}</style>
        </>
      )}
      
      {/* Common Header */}
      <CommonHeader showEditButton={true} />

      <main className="main-content">
        {/* Info Cards Section */}
        <section className="info-cards-section">
          <div className="container">
            <div className="info-cards-grid">
              {/* CEO Message Card */}
              <div 
                className="info-card ceo-message-card" 
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('Chairman card mouse down, current state:', showChairmanDropdown);
                  setShowChairmanDropdown(prev => {
                    console.log('Toggling from', prev, 'to', !prev);
                    return !prev;
                  });
                }}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                <div className="ceo-message-content">
                  <img 
                    src={chairmanData ? chairmanService.getImageUrl(chairmanData.icon) : "/assets/images/md-ceo.svg"} 
                    alt={chairmanData?.ceo_position || "Chairman"} 
                    className="ceo-image"
                  />
                  <div className="ceo-message-content">
                    <div className="ceo-message-text">
                      <p>Message from</p>
                      <h3>{chairmanData?.ceo_position || 'Chairman'}</h3>
                    </div>
                    <img 
                      src="/assets/images/down-arrow.svg" 
                      alt={showChairmanDropdown ? "Up Arrow" : "Down Arrow"} 
                      className="down-arrow"
                      style={{
                        transform: showChairmanDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s ease'
                      }}
                    />
                    <img src="/assets/images/wave.svg" alt="Wave" className="wave" />
                  </div>
                </div>
                <div className="ceo-message-dropdown" style={{ 
                  display: showChairmanDropdown ? 'block' : 'none'
                }}>
                  <div className="ceo-testimonial-card">
                    <div className="testimonial-content">
                      <div className="testimonial-image">
                        <img 
                          src={chairmanData ? chairmanService.getImageUrl(chairmanData.icon) : "/assets/images/md-ceo.svg"} 
                          alt={chairmanData?.ceo_name || "Rishabh Sethi"} 
                          className="ceo-photo"
                        />
                      </div>
                      <div className="testimonial-text">
                        <div className="quote-text">
                          <p>{chairmanData?.content || "As we continue to grow and evolve, I want to take a moment to express my gratitude to each one of you. Your dedication, creativity, and teamwork are what fuel our success. Every day, I am inspired by your passion and commitment to excellence, and together, we are building a future that we can all be proud of."}</p>
                          <div className="quote-icon">
                            <img src="/assets/images/quote.svg" alt="Quote Icon" />
                          </div>
                        </div>
                        <div className="ceo-info">
                          <div className="ceo-name">{chairmanData?.ceo_name || 'RISHABH SETHI'}</div>
                          <div className="ceo-title">{chairmanData?.ceo_position || 'MD & CEO'}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="vision-mission-cards">
                {/* Dharma Card */}
                <div 
                  className="info-card dharma-tenet-redesign clickable-card"
                  onClick={() => setShowDharmaModal(true)}
                  style={{ 
                    cursor: 'pointer',
                    borderTop: '4px solid #dc3545',
                    background: '#fff',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '80px',
                    position: 'relative',
                    padding: '12px 20px 0 20px',
                    overflow: 'visible'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                  }}
                >
                  <div style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    height: '100%',
                    paddingBottom: '35px'
                  }}>
                    <p style={{ 
                      fontSize: '13px', 
                      lineHeight: '1.4',
                      color: '#333',
                      margin: 0
                    }}>
                      Principles guiding ethical conduct, accountability, and organizational excellence.
                    </p>
                  </div>
                  <div style={{ 
                    position: 'absolute',
                    bottom: '0',
                    left: '0',
                    background: '#dc3545',
                    padding: '8px 20px',
                    borderRadius: '0 6px 0 8px'
                  }}>
                    <h4 style={{ 
                      color: '#fff',
                      margin: 0,
                      fontSize: '15px',
                      fontWeight: '600'
                    }}>Dharma</h4>
                  </div>
                </div>

                {/* Tenets Card */}
                <div 
                  className="info-card dharma-tenet-redesign clickable-card"
                  onClick={() => setShowTenetsModal(true)}
                  style={{ 
                    cursor: 'pointer',
                    borderTop: '4px solid #007bff',
                    background: '#fff',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '80px',
                    position: 'relative',
                    padding: '12px 20px 0 20px',
                    overflow: 'visible'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                  }}
                >
                  <div style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    height: '100%',
                    paddingBottom: '35px'
                  }}>
                    <p style={{ 
                      fontSize: '13px', 
                      lineHeight: '1.4',
                      color: '#333',
                      margin: 0
                    }}>
                      Core beliefs guiding behavior, decision-making, and organizational culture.
                    </p>
                  </div>
                  <div style={{ 
                    position: 'absolute',
                    bottom: '0',
                    left: '0',
                    background: '#007bff',
                    padding: '8px 20px',
                    borderRadius: '0 6px 0 8px'
                  }}>
                    <h4 style={{ 
                      color: '#fff',
                      margin: 0,
                      fontSize: '15px',
                      fontWeight: '600'
                    }}>Tenets</h4>
                  </div>
                </div>
              </div>
              <div 
                className="info-card ceo-message-card chairmain-message-card" 
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('CEO card mouse down, current state:', showCeoDropdown);
                  setShowCeoDropdown(prev => {
                    console.log('Toggling from', prev, 'to', !prev);
                    return !prev;
                  });
                }}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                <div className="ceo-message-content">
                  <img 
                    src={ceoData ? ceoService.getImageUrl(ceoData.icon) : "/assets/images/md-ceo.svg"} 
                    alt={ceoData?.ceo_position || "MD & CEO"} 
                    className="ceo-image"
                  />
                  <div className="ceo-message-content">
                    <div className="ceo-message-text">
                      <p>Message from</p>
                      <h3>{ceoData?.ceo_position || 'MD & CEO'}</h3>
                    </div>
                    <img 
                      src="/assets/images/down-arrow.svg" 
                      alt={showCeoDropdown ? "Up Arrow" : "Down Arrow"} 
                      className="down-arrow"
                      style={{
                        transform: showCeoDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s ease'
                      }}
                    />
                    <img src="/assets/images/wave.svg" alt="Wave" className="wave" />
                  </div>
                </div>
                <div className="ceo-message-dropdown" style={{ 
                  display: showCeoDropdown ? 'block' : 'none'
                }}>
                  <div className="ceo-testimonial-card">
                    <div className="testimonial-content">
                      <div className="testimonial-image">
                        <img 
                          src={ceoData ? ceoService.getImageUrl(ceoData.icon) : "/assets/images/md-ceo.svg"} 
                          alt={ceoData?.ceo_name || "Chairman"} 
                          className="ceo-photo"
                        />
                      </div>
                      <div className="testimonial-text">
                        <div className="quote-text">
                          <p>{ceoData?.content || "As we embrace 2025 that has just ushered in, I take this opportunity to wish you and your family, a Happy New Year. Hope this is the year of health, happiness, and success stories"}</p>
                          <div className="quote-icon">
                            <img src="/assets/images/quote.svg" alt="Quote Icon" />
                          </div>
                        </div>
                        <div className="ceo-info">
                          <div className="ceo-name">{ceoData?.ceo_name || 'NEERAJ KUMAR'}</div>
                          <div className="ceo-title">{ceoData?.ceo_position || 'Chairman'}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Access Essentials */}
        <section className="access-essentials-section">
          <div className="container">
            <div className="access-essentials">
              <div className="title">Application<br />Quick Links</div>
              <div className="apps">
                {applicationData.map((app) => (
                  <div 
                    key={app.id} 
                    className="app-card"
                    onClick={() => handleApplicationClick(app.link || '')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="app-icon">
                      <img 
                        src={applicationService.getImageUrl(app.icon || null)} 
                        alt={app.title || "Application"} 
                      />
                    </div>
                    <span>{app.title}</span>
                    <img src="/assets/images/wave.svg" alt="Wave" className="wave" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Main Cards Section */}
        <section className="main-cards-section">
          <div className="container">
            <div className="main-cards-grid">
              {/* Image Banner Carousel - Dynamic from API */}
              <div className="water-treatment-carousel">
                <ImageCarousel />
              </div>

              {/* Employee Card Container */}
              <div className="employee-card" style={{ position: 'relative' }}>
                {/* New Joinee Carousel */}
                <div className="new-joinee-carousel">
                  {newJoinees.length > 0 ? (
                    <Slider
                      key={`new-joinee-slider-${sliderKey}`}
                      dots={true}
                      infinite={newJoinees.length > 1}
                      speed={500}
                      slidesToShow={1}
                      slidesToScroll={1}
                      autoplay={newJoinees.length > 1}
                      autoplaySpeed={4500}
                      arrows={false}
                      adaptiveHeight={false}
                      lazyLoad="ondemand"
                      customPaging={(i) => (
                        <div className="custom-dot">
                          <span></span>
                        </div>
                      )}
                      dotsClass="slick-dots custom-dots"
                    >
                      {newJoinees.map((user) => (
                        <div key={user.id}>
                          <div className="card new-joinee-card">
                            <h4>New Joinee</h4>
                            <div className="profile-info">
                              <img 
                                src={getProfilePictureUrl(user)} 
                                alt={user.employeeName} 
                                className="profile-pic"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user.employeeName)}&background=007bff&color=fff&size=200`;
                                }}
                              />
                              <h5>{user.employeeName}</h5>
                              <p>{user.designation || 'Employee'}</p>
                              <p>{user.email}</p>
                              <p className="joined-date">joined on: {user.dateOfJoining ? formatDate(user.dateOfJoining) : 'N/A'}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </Slider>
                  ) : (
                    <div className="card new-joinee-card">
                      <h4>New Joinee</h4>
                      <div className="profile-info" style={{ padding: '40px 20px', textAlign: 'center', color: '#6c757d' }}>
                        <p>No new joiners in the last 7 days</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Birthdays Carousel */}
                <div className="birthdays-carousel">
                  {birthdayUsers.length > 0 ? (
                    <Slider
                      key={`birthday-slider-${sliderKey}`}
                      dots={true}
                      infinite={birthdayUsers.length > 1}
                      speed={500}
                      slidesToShow={1}
                      slidesToScroll={1}
                      autoplay={birthdayUsers.length > 1}
                      autoplaySpeed={5000}
                      arrows={false}
                      adaptiveHeight={false}
                      lazyLoad="ondemand"
                      customPaging={(i) => (
                        <div className="custom-dot">
                          <span></span>
                        </div>
                      )}
                      dotsClass="slick-dots custom-dots"
                    >
                      {birthdayUsers.map((user) => (
                        <div key={user.id}>
                          <div className="card birthdays-card">
                            <h4>Birthdays</h4>
                            <div className="profile-info">
                              <img 
                                src={getProfilePictureUrl(user)} 
                                alt={user.employeeName} 
                                className="profile-pic"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user.employeeName)}&background=007bff&color=fff&size=200`;
                                }}
                              />
                              <h5>{user.employeeName}</h5>
                              <p>Birthday Today • {user.designation || 'Employee'}{user.department ? ` • ${user.department}` : ''}</p>
                              <button className="wish-btn" onClick={() => sendBirthdayWish(user)}><span className="icon-send"></span> Wish</button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </Slider>
                  ) : (
                    <div className="card birthdays-card">
                      <h4>Birthdays</h4>
                      <div className="profile-info" style={{ padding: '40px 20px', textAlign: 'center', color: '#6c757d' }}>
                        <p>No birthdays today</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Work Anniversaries Carousel */}
                <div className="birthdays-carousel">
                  {anniversaryUsers.length > 0 ? (
                    <Slider
                      key={`anniversary-slider-${sliderKey}`}
                      dots={true}
                      infinite={anniversaryUsers.length > 1}
                      speed={500}
                      slidesToShow={1}
                      slidesToScroll={1}
                      autoplay={anniversaryUsers.length > 1}
                      autoplaySpeed={5000}
                      arrows={false}
                      adaptiveHeight={false}
                      lazyLoad="ondemand"
                      customPaging={(i) => (
                        <div className="custom-dot">
                          <span></span>
                        </div>
                      )}
                      dotsClass="slick-dots custom-dots"
                    >
                      {anniversaryUsers.map((user) => (
                        <div key={user.id}>
                          <div className="card birthdays-card">
                            <h4>Work Anniversaries</h4>
                            <div className="profile-info">
                              <img 
                                src={getProfilePictureUrl(user)} 
                                alt={user.employeeName} 
                                className="profile-pic"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user.employeeName)}&background=007bff&color=fff&size=200`;
                                }}
                              />
                              <h5>{user.employeeName}</h5>
                              <p>{user.dateOfJoining && getYearsOfService(user.dateOfJoining)} Year{getYearsOfService(user.dateOfJoining || '') !== 1 ? 's' : ''} Today • {user.designation || 'Employee'}{user.department ? ` • ${user.department}` : ''}</p>
                              <button className="wish-btn" onClick={() => sendAnniversaryWish(user)}><span className="icon-send"></span> Wish</button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </Slider>
                  ) : (
                    <div className="card birthdays-card">
                      <h4>Work Anniversaries</h4>
                      <div className="profile-info" style={{ padding: '40px 20px', textAlign: 'center', color: '#6c757d' }}>
                        <p>No work anniversaries today</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Image Carousel Modal */}
        {showCarouselModal && (
          <div className="carousel-modal-overlay" onClick={() => setShowCarouselModal(false)}>
            <div className="carousel-modal-container" onClick={(e) => e.stopPropagation()}>
              <button 
                className="carousel-modal-close" 
                onClick={() => setShowCarouselModal(false)}
              >
                &times;
              </button>
              <ImageCarousel />
            </div>
          </div>
        )}

        {/* Core Values, Vision & Mission Carousel */}
        <section className="core-values-section">
          <div className="container">
            <Swiper
              modules={[Navigation, Pagination, Autoplay]}
              spaceBetween={30}
              slidesPerView={1}
              navigation
              pagination={{ clickable: true }}
              autoplay={{
                delay: 5000,
                disableOnInteraction: false,
              }}
              className="values-carousel"
            >
              {/* Slide 1: Vision */}
              <SwiperSlide>
                <div className="core-values-content">
                  <div className="section-header">
                    <h2>OUR VISION</h2>
                    <p>Shaping the future with <span>innovation</span> and <span>excellence</span></p>
                  </div>
                  <div className="vision-mission-content">
                    <div className="vision-mission-card">
                      <div className="vision-mission-icon">
                        <img src="/assets/images/rocket-lunch.gif" alt="Vision" />
                      </div>
                      <div className="vision-mission-text">
                        <h3>Leading the Way Forward</h3>
                        <p>
                          To be the most trusted and innovative water infrastructure solutions provider,
                          transforming lives and communities through sustainable water management practices.
                          We envision a future where every individual has access to clean, safe water,
                          and where our solutions contribute to environmental sustainability and social progress.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </SwiperSlide>

              {/* Slide 2: Mission */}
              <SwiperSlide>
                <div className="core-values-content">
                  <div className="section-header">
                    <h2>OUR MISSION</h2>
                    <p>Committed to <span>excellence</span> in everything we do</p>
                  </div>
                  <div className="vision-mission-content">
                    <div className="vision-mission-card">
                      <div className="vision-mission-icon">
                        <img src="/assets/images/astronaut-flag.gif" alt="Mission" />
                      </div>
                      <div className="vision-mission-text">
                        <h3>Our Commitment to Excellence</h3>
                        <p>
                          To deliver world-class water infrastructure solutions through innovation,
                          technology, and sustainable practices. We are committed to creating value
                          for our stakeholders while ensuring the highest standards of quality,
                          safety, and environmental responsibility. Our mission is to empower communities
                          with reliable water solutions that enhance quality of life and support economic growth.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </SwiperSlide>

              {/* Slide 3: Core Values */}
              <SwiperSlide>
                <div className="core-values-content">
                  <div className="section-header">
                    <h2>OUR CORE VALUES</h2>
                    <p>Let's <span>LIVE</span> our Core Values and <span>REAP</span> the success</p>
                  </div>
                  <div className="core-values-grid-2x2">
                    <div className="value-card-horizontal reliability">
                      <div className="value-icon-circle">
                        <img src="/assets/images/preseverance.png" alt="RELIABILITY" />
                      </div>
                      <div className="value-text-content">
                        <p>We are trustworthy and reliable in thought and action</p>
                        <div className="value-badge reliability-badge">RELIABILITY</div>
                      </div>
                    </div>
                    <div className="value-card-horizontal excellence">
                      <div className="value-icon-circle">
                        <img src="/assets/images/preseverance.png" alt="EXCELLENCE" />
                      </div>
                      <div className="value-text-content">
                        <p>We strive for goals with passion to achieve the set benchmarks</p>
                        <div className="value-badge excellence-badge">EXCELLENCE</div>
                      </div>
                    </div>
                    <div className="value-card-horizontal agility">
                      <div className="value-icon-circle">
                        <img src="/assets/images/preseverance.png" alt="AGILITY" />
                      </div>
                      <div className="value-text-content">
                        <p>We are agile un approach to navigate through changes and deliver results</p>
                        <div className="value-badge agility-badge">AGILITY</div>
                      </div>
                    </div>
                    <div className="value-card-horizontal perseverance">
                      <div className="value-icon-circle">
                        <img src="/assets/images/preseverance.png" alt="PERSEVERANCE" />
                      </div>
                      <div className="value-text-content">
                        <p>We ensure the resilience and commitment to overcome challenge</p>
                        <div className="value-badge perseverance-badge">PERSEVERANCE</div>
                      </div>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            </Swiper>
          </div>
        </section>

        {/* JWIL Updates */}
        <section className="jwil-updates-section">
          <div className="container">
            <div className="updates-layout full-width">
              {/* Main Updates Content */}
              <div className="updates-main">
                {/* Bootstrap Tabs */}
                <ul className="nav nav-tabs updates-tabs-bootstrap" id="updatesTabs" role="tablist">
                  {/* Mobile Dropdown */}
                  <li className="nav-item dropdown d-md-none w-100">
                    <button 
                      className="nav-link dropdown-toggle active w-100 text-start d-flex justify-content-between align-items-center" 
                      data-bs-toggle="dropdown" 
                      aria-expanded="false"
                      style={{
                        backgroundColor: 'white',
                        border: '1px solid #dee2e6',
                        borderRadius: '4px',
                        padding: '12px 16px',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#333'
                      }}
                    >
                      <span>{activeTab}</span>
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
                      </svg>
                    </button>
                    <ul className="dropdown-menu w-100">
                      <li>
                        <button 
                          className={`dropdown-item ${activeTab === 'JWIL Latest Updates' ? 'active' : ''}`}
                          onClick={() => handleTabChange('JWIL Latest Updates')}
                        >
                          JWIL Latest Updates
                        </button>
                      </li>
                      <li>
                        <button 
                          className={`dropdown-item ${activeTab === 'Know Your Company' ? 'active' : ''}`}
                          onClick={() => handleTabChange('Know Your Company')}
                        >
                          Know Your Company
                        </button>
                      </li>
                      <li>
                        <button 
                          className={`dropdown-item ${activeTab === 'Company Policies' ? 'active' : ''}`}
                          onClick={() => handleTabChange('Company Policies')}
                        >
                          Company Policies
                        </button>
                      </li>
                      <li>
                        <button 
                          className={`dropdown-item ${activeTab === 'E-Library' ? 'active' : ''}`}
                          onClick={() => handleTabChange('E-Library')}
                        >
                          E-Library
                        </button>
                      </li>
                      <li>
                        <button 
                          className={`dropdown-item ${activeTab === 'Gallery' ? 'active' : ''}`}
                          onClick={() => handleTabChange('Gallery')}
                        >
                          Gallery
                        </button>
                      </li>
                      <li>
                        <button 
                          className={`dropdown-item ${activeTab === 'Newsletter' ? 'active' : ''}`}
                          onClick={() => handleTabChange('Newsletter')}
                        >
                          Newsletter
                        </button>
                      </li>
                      <li>
                        <button 
                          className={`dropdown-item ${activeTab === 'Reward and Recognitions' ? 'active' : ''}`}
                          onClick={() => handleTabChange('Reward and Recognitions')}
                        >
                          Reward and Recognitions
                        </button>
                      </li>
                    </ul>
                  </li>

                  {/* Desktop Tabs */}
                  <li className="nav-item d-none d-md-block">
                    <button 
                      className={`nav-link ${activeTab === 'JWIL Latest Updates' ? 'active' : ''}`}
                      onClick={() => handleTabChange('JWIL Latest Updates')}
                    >
                      JWIL Latest Updates
                    </button>
                  </li>
                  <li className="nav-item d-none d-md-block">
                    <button 
                      className={`nav-link ${activeTab === 'Know Your Company' ? 'active' : ''}`}
                      onClick={() => handleTabChange('Know Your Company')}
                    >
                      Know Your Company
                    </button>
                  </li>
                  <li className="nav-item d-none d-md-block">
                    <button 
                      className={`nav-link ${activeTab === 'Company Policies' ? 'active' : ''}`}
                      onClick={() => handleTabChange('Company Policies')}
                    >
                      Company Policies
                    </button>
                  </li>
                  <li className="nav-item d-none d-md-block">
                    <button 
                      className={`nav-link ${activeTab === 'E-Library' ? 'active' : ''}`}
                      onClick={() => handleTabChange('E-Library')}
                    >
                      E-Library
                    </button>
                  </li>
                  <li className="nav-item d-none d-md-block">
                    <button 
                      className={`nav-link ${activeTab === 'Gallery' ? 'active' : ''}`}
                      onClick={() => handleTabChange('Gallery')}
                    >
                      Gallery
                    </button>
                  </li>
                  <li className="nav-item d-none d-md-block">
                    <button 
                      className={`nav-link ${activeTab === 'Newsletter' ? 'active' : ''}`}
                      onClick={() => handleTabChange('Newsletter')}
                    >
                      Newsletter
                    </button>
                  </li>
                  <li className="nav-item d-none d-md-block">
                    <button 
                      className={`nav-link ${activeTab === 'Reward and Recognitions' ? 'active' : ''}`}
                      onClick={() => handleTabChange('Reward and Recognitions')}
                    >
                      Reward and Recognitions
                    </button>
                  </li>
                </ul>

                {/* Tab Content */}
                <div className="tab-content" id="updatesTabContent">
                  {/* Conditional Content Based on Active Tab */}
                {activeTab === 'JWIL Latest Updates' && (
                  <div className="updates-container">
                    {jwilUpdates.length === 0 ? (
                      <EmptyState 
                        title="No updates available"
                        message="Check back later for new updates"
                        icon="📰"
                      />
                    ) : (
                    <div className="updates-list">
                      {(showAllUpdates ? jwilUpdates : jwilUpdates.slice(0, 6)).map((update: JwilUpdate) => (
                        <div 
                          key={update.id} 
                          className="update-item"
                          onClick={() => update.link && window.open(update.link, '_blank')}
                          style={{ cursor: update.link ? 'pointer' : 'default' }}
                        >
                          <img 
                            src={update.image ? `${jwilUpdatesService.getImageUrl(update.image)}?t=${imageRefreshKey}` : "/assets/images/logo.png"} 
                            alt={update.title} 
                            className="update-img"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = "/assets/images/logo.png";
                            }}
                          />
                          <div className="update-content">
                            <h5>{update.title}</h5>
                            <p>{update.description}</p>
                            <span className="update-date">{update.date}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    )}
                    {jwilUpdates.length > 0 && !showAllUpdates && jwilUpdates.length > 6 && (
                      <div className="view-all-container">
                        <button 
                          className="view-all-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowAllUpdates(true);
                          }}
                        >
                          View All
                        </button>
                      </div>
                    )}
                    {showAllUpdates && (
                      <div className="view-all-container">
                        <button 
                          className="view-all-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowAllUpdates(false);
                          }}
                        >
                          Show Less
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Newsletter Tab Content */}
                {activeTab === 'Newsletter' && (
                  <NewsletterDisplay />
                )}

                {/* Reward and Recognitions Tab Content */}
                {activeTab === 'Reward and Recognitions' && (
                  <div className="reward-recognition-container">
                    {contentLoading ? (
                      <div className="loading-container">
                        <p>Loading rewards and recognitions...</p>
                      </div>
                    ) : rewardCategories.length === 0 ? (
                      <EmptyState 
                        title="No reward categories available"
                        message="Check back later for recognitions"
                        icon="🏆"
                      />
                    ) : (
                      <div className="reap-accordion">
                        {rewardCategories.map((category) => {
                          const categoryRecognitions = rewardRecognitions.filter(r => r.category_id === category.id);
                          const isExpanded = expandedCategory === category.id;
                          
                          return (
                            <div key={category.id} className="reap-category-section">
                              <div 
                                className="reap-category-header"
                                onClick={() => setExpandedCategory(isExpanded ? null : category.id)}
                                style={{ borderLeftColor: category.color_code }}
                              >
                                <div className="category-info">
                                  <div 
                                    className="category-letter"
                                    style={{ backgroundColor: category.color_code }}
                                  >
                                    {category.name.charAt(0)}
                                  </div>
                                  <div className="category-details">
                                    <h3>
                                      {category.name}
                                      <sup style={{ 
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '20px',
                                        height: '20px',
                                        borderRadius: '50%',
                                        backgroundColor: '#007bff',
                                        color: 'white',
                                        fontSize: '11px',
                                        fontWeight: 'bold',
                                        marginLeft: '8px',
                                        verticalAlign: 'super'
                                      }}>
                                        {categoryRecognitions.length}
                                      </sup>
                                    </h3>
                                    <p>{category.description}</p>
                                  </div>
                                </div>
                                <div className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
                                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                </div>
                              </div>
                              
                              {isExpanded && (
                                <div className="reap-category-content">
                                  {categoryRecognitions.length === 0 ? (
                                    <div className="no-recognitions">
                                      <p>No recognitions in this category yet</p>
                                    </div>
                                  ) : (
                                    <div className="recognition-cards-grid">
                                      {categoryRecognitions.map((recognition) => (
                                        <div key={recognition.id} className="recognition-card">
                                          <div className="employee-photo-container">
                                            <img
                                              src={recognition.employee_photo ? 
                                                rewardRecognitionService.getPhotoUrl(recognition.employee_photo) : 
                                                '/assets/images/profile-avatar.jpg'
                                              }
                                              alt={recognition.employee_name}
                                              className="employee-photo"
                                              onError={(e) => {
                                                (e.target as HTMLImageElement).src = '/assets/images/profile-avatar.jpg';
                                              }}
                                            />
                                            <div 
                                              className="subcategory-tag"
                                              style={{ backgroundColor: category.color_code }}
                                            >
                                              {recognition.subcategory?.name || 'Recognition'}
                                            </div>
                                            {recognition.is_featured && (
                                              <div className="featured-star">★</div>
                                            )}
                                          </div>
                                          <div className="recognition-info">
                                            <h4>{recognition.employee_name}</h4>
                                            <p className="employee-id">{recognition.employee_id}</p>
                                            <p className="recognition-description">{recognition.description}</p>
                                            {recognition.achievement_details && (
                                              <p className="achievement-details">{recognition.achievement_details}</p>
                                            )}
                                            <div className="award-date">
                                              {new Date(recognition.award_date).toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: 'short',
                                                day: 'numeric'
                                              })}
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Company Policies Tab Content */}
                {activeTab === 'Company Policies' && (
                  <div className="reward-recognition-container">
                    {contentLoading ? (
                      <div className="loading-container">
                        <p>Loading company policies...</p>
                      </div>
                    ) : policyCategories.length === 0 ? (
                      <EmptyState 
                        title="No policy categories available"
                        message="Check back later for policies"
                        icon="📋"
                      />
                    ) : (
                      <div className="reap-accordion">
                        {policyCategories.map((category) => {
                          const categoryPolicies = companyPoliciesData.filter(p => p.category === category.name);
                          const isExpanded = expandedPolicyCategory === category.id;
                          
                          return (
                            <div key={category.id} className="reap-category-section">
                              <div 
                                className="reap-category-header"
                                onClick={() => setExpandedPolicyCategory(isExpanded ? null : category.id)}
                                style={{ borderLeftColor: category.color }}
                              >
                                <div className="category-info">
                                  <div 
                                    className="category-letter"
                                    style={{ backgroundColor: category.color }}
                                  >
                                    {category.name.charAt(0)}
                                  </div>
                                  <div className="category-details">
                                    <h3>
                                      {category.name}
                                      <sup style={{ 
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '20px',
                                        height: '20px',
                                        borderRadius: '50%',
                                        backgroundColor: '#007bff',
                                        color: 'white',
                                        fontSize: '11px',
                                        fontWeight: 'bold',
                                        marginLeft: '8px',
                                        verticalAlign: 'super'
                                      }}>
                                        {categoryPolicies.length}
                                      </sup>
                                    </h3>
                                    <p>View all policies in this category</p>
                                  </div>
                                </div>
                                <div className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
                                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                </div>
                              </div>
                              
                              {isExpanded && (
                                <div className="reap-category-content">
                                  {categoryPolicies.length === 0 ? (
                                    <div className="no-recognitions">
                                      <p>No policies in this category yet</p>
                                    </div>
                                  ) : (
                                    <>
                                      <div className="policy-grid-container">
                                        {categoryPolicies.map((policy: CompanyPolicy) => (
                                          <div 
                                            key={policy.id}
                                            className="policy-grid-item"
                                            onClick={() => {
                                              if (policy.file_path) {
                                                window.open(policy.file_path, '_blank');
                                              } else if (policy.link) {
                                                window.open(policy.link, '_blank');
                                              }
                                            }}
                                          >
                                            <div className="policy-icon-box">
                                              <img 
                                                src={policy.file_path ? '/assets/images/doc-icon.svg' : '/assets/images/link-icon.svg'} 
                                                alt={policy.file_path ? 'Document' : 'Link'}
                                              />
                                            </div>
                                            <div className="policy-title">
                                              {policy.title}
                                            </div>
                                            <div className="policy-date">
                                              {policy.date}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                      <style>{`
                                        .policy-grid-container {
                                          display: grid;
                                          grid-template-columns: repeat(7, 1fr);
                                          gap: 8px;
                                          padding: 8px 0;
                                        }
                                        .policy-grid-item {
                                          display: flex;
                                          flex-direction: column;
                                          align-items: center;
                                          gap: 4px;
                                          padding: 8px 6px;
                                          background-color: #f8f9fa;
                                          border-radius: 6px;
                                          cursor: pointer;
                                          transition: all 0.2s ease;
                                          border: 1px solid #e9ecef;
                                          text-align: center;
                                          min-height: 85px;
                                        }
                                        .policy-grid-item:hover {
                                          background-color: #e9ecef;
                                          transform: translateY(-2px);
                                          box-shadow: 0 3px 6px rgba(0,0,0,0.1);
                                        }
                                        .policy-icon-box {
                                          width: 28px;
                                          height: 28px;
                                          display: flex;
                                          align-items: center;
                                          justify-content: center;
                                          background-color: white;
                                          border-radius: 5px;
                                          border: 1px solid #dee2e6;
                                          flex-shrink: 0;
                                        }
                                        .policy-icon-box img {
                                          width: 16px;
                                          height: 16px;
                                        }
                                        .policy-title {
                                          font-size: 11px;
                                          font-weight: 600;
                                          color: #333;
                                          line-height: 1.2;
                                          overflow: hidden;
                                          text-overflow: ellipsis;
                                          display: -webkit-box;
                                          -webkit-line-clamp: 2;
                                          -webkit-box-orient: vertical;
                                          flex: 1;
                                        }
                                        .policy-date {
                                          font-size: 9px;
                                          color: #6c757d;
                                          margin-top: auto;
                                        }
                                        @media (max-width: 1400px) {
                                          .policy-grid-container {
                                            grid-template-columns: repeat(6, 1fr);
                                          }
                                        }
                                        @media (max-width: 1200px) {
                                          .policy-grid-container {
                                            grid-template-columns: repeat(5, 1fr);
                                          }
                                        }
                                        @media (max-width: 992px) {
                                          .policy-grid-container {
                                            grid-template-columns: repeat(4, 1fr);
                                          }
                                        }
                                        @media (max-width: 768px) {
                                          .policy-grid-container {
                                            grid-template-columns: repeat(3, 1fr);
                                            gap: 6px;
                                          }
                                          .policy-grid-item {
                                            padding: 7px 5px;
                                            min-height: 80px;
                                          }
                                        }
                                        @media (max-width: 480px) {
                                          .policy-grid-container {
                                            grid-template-columns: repeat(3, 1fr);
                                            gap: 5px;
                                          }
                                          .policy-grid-item {
                                            padding: 6px 4px;
                                            min-height: 75px;
                                          }
                                          .policy-icon-box {
                                            width: 24px;
                                            height: 24px;
                                          }
                                          .policy-icon-box img {
                                            width: 14px;
                                            height: 14px;
                                          }
                                          .policy-title {
                                            font-size: 10px;
                                          }
                                          .policy-date {
                                            font-size: 8px;
                                          }
                                        }
                                      `}</style>
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Snippets Tab Content - Facebook-style Albums */}
                {activeTab === 'Gallery' && (
                  <div className="snippets-container">
                    {contentLoading ? (
                      <div style={{ padding: '40px', textAlign: 'center' }}>
                        <Loader />
                      </div>
                    ) : snippetAlbums.length === 0 && othersMedia.length === 0 ? (
                      <EmptyState 
                        title="No albums available"
                        message="Check back later for event photos and videos"
                        icon="📸"
                      />
                    ) : (
                      <>
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                          gap: '20px',
                          padding: '10px 0'
                        }}>
                          {/* Others Album - Media without album_id */}
                          {othersMedia.length > 0 && (
                            <div
                              key="others"
                              onClick={() => {
                                setSelectedAlbumMedia({
                                  id: 0,
                                  event_name: 'Others',
                                  description: 'Uncategorized photos and videos',
                                  is_visible: true,
                                  sort_order: 0,
                                  created_at: new Date(),
                                  updated_at: new Date(),
                                  media: othersMedia
                                } as any);
                                setShowAlbumModal(true);
                              }}
                              style={{
                                backgroundColor: '#fff',
                                borderRadius: '12px',
                                overflow: 'hidden',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                border: '2px solid #6c757d'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-4px)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                              }}
                            >
                              {/* Others Cover - Show first media or placeholder */}
                              <div style={{
                                position: 'relative',
                                width: '100%',
                                paddingTop: '75%',
                                backgroundColor: '#f0f2f5',
                                overflow: 'hidden'
                              }}>
                                {othersMedia[0]?.file_path ? (
                                  othersMedia[0].media_type === 'image' ? (
                                    <img
                                      src={snippetAlbumService.getFileUrl(othersMedia[0].file_path)}
                                      alt="Others"
                                      style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'fill'
                                      }}
                                      onError={(e) => {
                                        (e.target as HTMLImageElement).src = "/assets/images/logo.png";
                                      }}
                                    />
                                  ) : (
                                    <video
                                      src={snippetAlbumService.getFileUrl(othersMedia[0].file_path)}
                                      style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover'
                                      }}
                                    />
                                  )
                                ) : (
                                  <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '48px',
                                    color: '#65676b'
                                  }}>
                                    📂
                                  </div>
                                )}
                                {/* Media Count Badge */}
                                <div style={{
                                  position: 'absolute',
                                  bottom: '10px',
                                  right: '10px',
                                  backgroundColor: 'rgba(108, 117, 125, 0.9)',
                                  color: 'white',
                                  padding: '4px 10px',
                                  borderRadius: '12px',
                                  fontSize: '13px',
                                  fontWeight: '600',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}>
                                  <span>📷</span>
                                  <span>{othersMedia.length}</span>
                                </div>
                              </div>
                              
                              {/* Others Info */}
                              <div style={{ padding: '12px 16px', backgroundColor: '#f8f9fa' }}>
                                <h5 style={{
                                  margin: '0 0 6px 0',
                                  fontSize: '16px',
                                  fontWeight: '600',
                                  color: '#050505',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  📂 Others
                                </h5>
                                <p style={{
                                  margin: '0',
                                  fontSize: '13px',
                                  color: '#65676b',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  lineHeight: '1.4'
                                }}>
                                  Uncategorized photos and videos
                                </p>
                              </div>
                            </div>
                          )}
                          
                          {/* Regular Albums */}
                          {snippetAlbums.map((album) => (
                            <div
                              key={album.id}
                              onClick={async () => {
                                try {
                                  const albumWithMedia = await snippetAlbumService.getAlbumWithMedia(album.id);
                                  setSelectedAlbumMedia(albumWithMedia);
                                  setShowAlbumModal(true);
                                } catch (err) {
                                  console.error('Error loading album:', err);
                                }
                              }}
                              style={{
                                backgroundColor: '#fff',
                                borderRadius: '12px',
                                overflow: 'hidden',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                border: '1px solid #e4e6eb'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-4px)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                              }}
                            >
                              {/* Album Cover */}
                              <div style={{
                                position: 'relative',
                                width: '100%',
                                paddingTop: '75%',
                                backgroundColor: '#f0f2f5',
                                overflow: 'hidden'
                              }}>
                                {album.cover_image ? (
                                  <img
                                    src={snippetAlbumService.getFileUrl(album.cover_image)}
                                    alt={album.event_name}
                                    style={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'fill'
                                    }}
                                    onError={(e) => {
                                      (e.target as HTMLImageElement).src = "/assets/images/logo.png";
                                    }}
                                  />
                                ) : (
                                  <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '48px',
                                    color: '#65676b'
                                  }}>
                                    📁
                                  </div>
                                )}
                                {/* Media Count Badge */}
                                {album.media_count && album.media_count > 0 && (
                                  <div style={{
                                    position: 'absolute',
                                    bottom: '10px',
                                    right: '10px',
                                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                    color: 'white',
                                    padding: '4px 10px',
                                    borderRadius: '12px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                  }}>
                                    <span>📷</span>
                                    <span>{album.media_count}</span>
                                  </div>
                                )}
                              </div>
                              
                              {/* Album Info */}
                              <div style={{ padding: '12px 16px' }}>
                                <h5 style={{
                                  margin: '0 0 6px 0',
                                  fontSize: '16px',
                                  fontWeight: '600',
                                  color: '#050505',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {album.event_name}
                                </h5>
                                {album.description && (
                                  <p style={{
                                    margin: '0 0 8px 0',
                                    fontSize: '13px',
                                    color: '#65676b',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    lineHeight: '1.4'
                                  }}>
                                    {album.description}
                                  </p>
                                )}
                                {album.event_date && (
                                  <div style={{
                                    fontSize: '12px',
                                    color: '#8a8d91',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                  }}>
                                    <span>📅</span>
                                    <span>{new Date(album.event_date).toLocaleDateString('en-US', { 
                                      year: 'numeric', 
                                      month: 'short', 
                                      day: 'numeric' 
                                    })}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Events Tab Content */}
                {activeTab === 'Events' && (
                  <div className="events-container">
                    {eventsData.length === 0 ? (
                      <EmptyState 
                        title="No upcoming events"
                        message="Check back later for new events"
                        icon="📅"
                      />
                    ) : (
                    <div className="events-grid" style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 320px))',
                      gap: '20px',
                      justifyContent: 'start'
                    }}>
                      {(showAllEvents ? eventsData : eventsData.slice(0, 6)).map((event: Event) => (
                        <div 
                          key={event.id} 
                          className="event-card"
                          onClick={() => event.link && window.open(event.link, '_blank')}
                          style={{ cursor: event.link ? 'pointer' : 'default', maxWidth: '320px' }}
                        >
                          <div className="event-image">
                            <img 
                              src={event.image ? `${eventsService.getImageUrl(event.image)}?t=${imageRefreshKey}` : "/assets/images/banner-image.jpg"} 
                              alt={event.title}
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = "/assets/images/banner-image.jpg";
                              }}
                            />
                            <div className="event-date-badge">
                              <span className="event-month">{eventsService.getMonthAbbr(event.event_date)}</span>
                              <span className="event-day">{eventsService.getDayNumber(event.event_date)}</span>
                            </div>
                          </div>
                          <div className="event-content">
                            <h3 className="event-title" style={{ fontWeight: '700' }}>{event.title}</h3>
                            <p className="event-description" style={{ marginBottom: '12px' }}>{event.description}</p>
                            
                            {/* Duration */}
                            <div style={{ 
                              fontSize: '13px', 
                              color: '#666', 
                              marginBottom: '10px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}>
                              <span className="icon-clock" style={{ fontSize: '14px' }}></span>
                              <span>{eventsService.getEventDuration(event.start_time, event.end_time)}</span>
                            </div>

                            {/* Price Tag and Location */}
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '10px',
                              marginBottom: '12px',
                              flexWrap: 'wrap'
                            }}>
                              {/* FREE or PAID Tag */}
                              <span style={{
                                display: 'inline-block',
                                padding: '4px 12px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: '600',
                                backgroundColor: event.price === null || event.price === 0 ? '#d4edda' : '#fff3cd',
                                color: event.price === null || event.price === 0 ? '#155724' : '#856404',
                                border: `1px solid ${event.price === null || event.price === 0 ? '#c3e6cb' : '#ffeaa7'}`
                              }}>
                                {event.price === null || event.price === 0 ? 'FREE' : `₹${event.price}`}
                              </span>

                              {/* Location with Map Icon */}
                              {event.location && (
                                <div style={{ 
                                  fontSize: '13px', 
                                  color: '#333',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px'
                                }}>
                                  <span style={{ fontSize: '14px' }}>📍</span>
                                  <span style={{ fontWeight: '600' }}>{event.location}</span>
                                </div>
                              )}
                            </div>

                            {/* Learn More Button - Only if link exists */}
                            {event.link && (
                              <div className="event-actions">
                                <button className="event-btn primary">Learn More</button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    )}
                    {eventsData.length > 0 && !showAllEvents && eventsData.length > 6 && (
                      <div className="view-all-container">
                        <button 
                          className="view-all-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowAllEvents(true);
                          }}
                        >
                          View All Events
                        </button>
                      </div>
                    )}
                    {showAllEvents && (
                      <div className="view-all-container">
                        <button 
                          className="view-all-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowAllEvents(false);
                          }}
                        >
                          Show Less
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Know Your Company Tab Content */}
                {activeTab === 'Know Your Company' && (
                  <div className="know-your-company-container">
                    {/* Company Description Section */}
                    <div style={{
                      background: 'linear-gradient(135deg, #0066cc 0%, #004c99 100%)',
                      padding: '30px 30px',
                      borderRadius: '8px',
                      marginBottom: '30px',
                      boxShadow: '0 2px 12px rgba(0, 102, 204, 0.15)'
                    }}>
                      <h2 style={{
                        color: 'white',
                        fontSize: '28px',
                        fontWeight: '700',
                        marginBottom: '15px',
                        textAlign: 'left',
                        letterSpacing: '0.3px'
                      }}>
                        We Engineer Water
                      </h2>
                      <p style={{
                        color: 'white',
                        fontSize: '15px',
                        lineHeight: '1.6',
                        textAlign: 'left',
                        maxWidth: '100%',
                        margin: '0',
                        fontWeight: '400',
                        opacity: '0.95'
                      }}>
                        JWIL Infra Ltd, a distinguished entity within the esteemed OP Jindal Group since its inception in 2006, stands as a comprehensive water management enterprise. Specializing in turnkey solutions for projects encompassing drinking water, irrigation, wastewater, and industrial effluent treatment, JWIL has exhibited its prowess across diverse geographies, including India, Africa, and South Asia. Boasting in-house design, engineering, and execution capabilities, the company leverages the formidable financial strength of the Jindal Group, with subsidiary Jindal SAW Limited fulfilling the pivotal role of supplying DI and MS pipes essential for water management projects.
                      </p>
                    </div>

                    {/* Our Leaders Section */}
                    <div className="our-leaders-section">
                      <div className="our-leaders-container">
                        <h2 className="our-leaders-title">Our Leaders</h2>
                        {leadersLoading ? (
                          <div className="our-leaders-loading">Loading leaders...</div>
                        ) : leaders.length === 0 ? (
                          <EmptyState 
                            title="No leaders available"
                            message="Check back later to meet our leadership team"
                            icon="👥"
                          />
                        ) : (
                          <div className="our-leaders-carousel">
                            <Swiper
                              key={leadersSliderKey}
                              modules={[Navigation, Pagination, Autoplay]}
                              spaceBetween={20}
                              slidesPerView={5}
                              slidesPerGroup={5}
                              speed={500}
                              autoplay={{
                                delay: 5000,
                                disableOnInteraction: false,
                              }}
                              pagination={{
                                clickable: true,
                                dynamicBullets: false,
                              }}
                              breakpoints={{
                                0: {
                                  slidesPerView: 1,
                                  slidesPerGroup: 1,
                                },
                                460: {
                                  slidesPerView: 2,
                                  slidesPerGroup: 2,
                                },
                                660: {
                                  slidesPerView: 2,
                                  slidesPerGroup: 2,
                                },
                                768: {
                                  slidesPerView: 3,
                                  slidesPerGroup: 3,
                                },
                                992: {
                                  slidesPerView: 4,
                                  slidesPerGroup: 4,
                                },
                                1024: {
                                  slidesPerView: 4,
                                  slidesPerGroup: 4,
                                },
                                1280: {
                                  slidesPerView: 5,
                                  slidesPerGroup: 5,
                                },
                              }}
                              className="our-leaders-swiper"
                            >
                              {leaders.map((leader) => (
                                <SwiperSlide key={leader.id}>
                                  <div className="our-leaders-slide">
                                    <div className="our-leaders-card">
                                      <div className="our-leaders-image-container">
                                        {leader.image ? (
                                          <img
                                            src={leader.image}
                                            alt={leader.name}
                                            className="our-leaders-image"
                                          />
                                        ) : (
                                          <div className="our-leaders-image-placeholder">
                                            <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                              <circle cx="12" cy="7" r="4" />
                                            </svg>
                                          </div>
                                        )}
                                      </div>
                                      <div className="our-leaders-info">
                                        <h3 className="our-leaders-name">{leader.name}</h3>
                                        <p className="our-leaders-designation">{leader.designation}</p>
                                      </div>
                                    </div>
                                  </div>
                                </SwiperSlide>
                              ))}
                            </Swiper>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* E-Library Tab Content */}
                {activeTab === 'E-Library' && (
                  <div className="reward-recognition-container">
                    {contentLoading ? (
                      <div className="loading-container">
                        <p>Loading e-library...</p>
                      </div>
                    ) : libraryCategories.length === 0 ? (
                      <EmptyState 
                        title="No library categories available"
                        message="Check back later for e-library items"
                        icon="📋"
                      />
                    ) : (
                      <div className="reap-accordion">
                        {libraryCategories.map((category) => {
                          const categoryLibraries = eLibrariesData.filter(p => p.category === category.name);
                          const isExpanded = expandedLibraryCategory === category.id;
                          
                          return (
                            <div key={category.id} className="reap-category-section">
                              <div 
                                className="reap-category-header"
                                onClick={() => setExpandedLibraryCategory(isExpanded ? null : category.id)}
                                style={{ borderLeftColor: category.color }}
                              >
                                <div className="category-info">
                                  <div 
                                    className="category-letter"
                                    style={{ backgroundColor: category.color }}
                                  >
                                    {category.name.charAt(0)}
                                  </div>
                                  <div className="category-details">
                                    <h3>
                                      {category.name}
                                      <sup style={{ 
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '20px',
                                        height: '20px',
                                        borderRadius: '50%',
                                        backgroundColor: '#007bff',
                                        color: 'white',
                                        fontSize: '11px',
                                        fontWeight: 'bold',
                                        marginLeft: '8px',
                                        verticalAlign: 'super'
                                      }}>
                                        {categoryLibraries.length}
                                      </sup>
                                    </h3>
                                    <p>View all e-library items in this category</p>
                                  </div>
                                </div>
                                <div className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
                                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                </div>
                              </div>
                              
                              {isExpanded && (
                                <div className="reap-category-content">
                                  {categoryLibraries.length === 0 ? (
                                    <div className="no-recognitions">
                                      <p>No e-library items in this category yet</p>
                                    </div>
                                  ) : (
                                    <>
                                      <div className="library-grid-container">
                                        {categoryLibraries.map((library: ELibrary) => (
                                          <div 
                                            key={library.id}
                                            className="library-grid-item"
                                            onClick={() => {
                                              if (library.file_path) {
                                                window.open(library.file_path, '_blank');
                                              } else if (library.link) {
                                                window.open(library.link, '_blank');
                                              }
                                            }}
                                          >
                                            <div className="library-icon-box">
                                              <img 
                                                src={library.file_path ? '/assets/images/doc-icon.svg' : '/assets/images/link-icon.svg'} 
                                                alt={library.file_path ? 'Document' : 'Link'}
                                              />
                                            </div>
                                            <div className="library-title">
                                              {library.title}
                                            </div>
                                            <div className="library-date">
                                              {library.date}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                      <style>{`
                                        .library-grid-container {
                                          display: grid;
                                          grid-template-columns: repeat(7, 1fr);
                                          gap: 8px;
                                          padding: 8px 0;
                                        }
                                        .library-grid-item {
                                          display: flex;
                                          flex-direction: column;
                                          align-items: center;
                                          gap: 4px;
                                          padding: 8px 6px;
                                          background-color: #f8f9fa;
                                          border-radius: 6px;
                                          cursor: pointer;
                                          transition: all 0.2s ease;
                                          border: 1px solid #e9ecef;
                                          text-align: center;
                                          min-height: 85px;
                                        }
                                        .library-grid-item:hover {
                                          background-color: #e9ecef;
                                          transform: translateY(-2px);
                                          box-shadow: 0 3px 6px rgba(0,0,0,0.1);
                                        }
                                        .library-icon-box {
                                          width: 28px;
                                          height: 28px;
                                          display: flex;
                                          align-items: center;
                                          justify-content: center;
                                          background-color: white;
                                          border-radius: 5px;
                                          border: 1px solid #dee2e6;
                                          flex-shrink: 0;
                                        }
                                        .library-icon-box img {
                                          width: 16px;
                                          height: 16px;
                                        }
                                        .library-title {
                                          font-size: 11px;
                                          font-weight: 600;
                                          color: #333;
                                          line-height: 1.2;
                                          overflow: hidden;
                                          text-overflow: ellipsis;
                                          display: -webkit-box;
                                          -webkit-line-clamp: 2;
                                          -webkit-box-orient: vertical;
                                          flex: 1;
                                        }
                                        .library-date {
                                          font-size: 9px;
                                          color: #6c757d;
                                          margin-top: auto;
                                        }
                                        @media (max-width: 1400px) {
                                          .library-grid-container {
                                            grid-template-columns: repeat(6, 1fr);
                                          }
                                        }
                                        @media (max-width: 1200px) {
                                          .library-grid-container {
                                            grid-template-columns: repeat(5, 1fr);
                                          }
                                        }
                                        @media (max-width: 992px) {
                                          .library-grid-container {
                                            grid-template-columns: repeat(4, 1fr);
                                          }
                                        }
                                        @media (max-width: 768px) {
                                          .library-grid-container {
                                            grid-template-columns: repeat(3, 1fr);
                                            gap: 6px;
                                          }
                                          .library-grid-item {
                                            padding: 7px 5px;
                                            min-height: 80px;
                                          }
                                        }
                                        @media (max-width: 480px) {
                                          .library-grid-container {
                                            grid-template-columns: repeat(3, 1fr);
                                            gap: 5px;
                                          }
                                          .library-grid-item {
                                            padding: 6px 4px;
                                            min-height: 75px;
                                          }
                                          .library-icon-box {
                                            width: 24px;
                                            height: 24px;
                                          }
                                          .library-icon-box img {
                                            width: 14px;
                                            height: 14px;
                                          }
                                          .library-title {
                                            font-size: 10px;
                                          }
                                          .library-date {
                                            font-size: 8px;
                                          }
                                        }
                                      `}</style>
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Other Tab Contents - Placeholder */}
                {activeTab !== 'JWIL Latest Updates'&& activeTab !== 'Newsletter' && activeTab !== 'Know Your Company' && activeTab !== 'Reward and Recognitions' && activeTab !== 'Company Policies' && activeTab !== 'E-Library' && activeTab !== 'Gallery' && activeTab !== 'Events' && (
                  <div className="tab-content-placeholder">
                    <h3>{activeTab}</h3>
                    <p>Content for {activeTab} will be displayed here.</p>
                  </div>
                )}
                </div>
              </div>

              {/* Sidebar - Company Metrics Commented Out */}
              {/* 
              <aside className="updates-sidebar">
                <div className="sidebar-card company-metrics-card">
                  <div className="card-header">
                    <h6>Company Metrics</h6>
                    <span className="icon-edit"></span>
                  </div>
                  <div className="metrics-grid">
                    <div className="metric-item">
                      <p>Total Expenses</p>
                      <div className="metric-value">42</div>
                      <div className="metric-change down"><span className="icon-down-arrow"></span> 12% Last Week</div>
                    </div>
                    <div className="metric-item">
                      <p>Revenue Generated</p>
                      <div className="metric-value">28.2 M</div>
                      <div className="metric-change up"><span className="icon-up-arrow"></span> 8% Last Month</div>
                    </div>
                    <div className="metric-item">
                      <p>Response Time</p>
                      <div className="metric-value">15.8 hrs</div>
                      <div className="metric-change up"><span className="icon-up-arrow"></span> 5% Last Week</div>
                    </div>
                    <div className="metric-item">
                      <p>Response Time</p>
                      <div className="metric-value">15.8 hrs</div>
                      <div className="metric-change up"><span className="icon-up-arrow"></span> 5% Last Week</div>
                    </div>
                  </div>
                </div>
              </aside>
              */}
            </div>
          </div>
        </section>

      {/* Image Modal */}
      {showImageModal && selectedMedia && (
        <div className="media-modal-overlay" onClick={closeImageModal}>
          <div className="media-modal-container" onClick={(e) => e.stopPropagation()}>
            <button className="media-modal-close" onClick={closeImageModal}>
              &times;
            </button>
            <img 
              src={selectedMedia.link} 
              alt={selectedMedia.title} 
              className="modal-image"
            />
            <div className="modal-caption">
              <h3>{selectedMedia.title}</h3>
              <p>{selectedMedia.description}</p>
            </div>
          </div>
        </div>
      )}

      {/* Video Modal */}
      {showVideoModal && selectedMedia && (
        <div className="media-modal-overlay" onClick={closeVideoModal}>
          <div className="media-modal-container video-modal" onClick={(e) => e.stopPropagation()}>
            <button className="media-modal-close" onClick={closeVideoModal}>
              &times;
            </button>
            <video
              src={selectedMedia.link}
              className="modal-video"
              controls
              autoPlay
              style={{ width: '100%', maxHeight: '70vh', backgroundColor: '#000' }}
            >
              Your browser does not support the video tag.
            </video>
            <div className="modal-caption">
              <h3>{selectedMedia.title}</h3>
              <p>{selectedMedia.description}</p>
            </div>
          </div>
        </div>
      )}

      {/* Album Modal - View Photos/Videos */}
      {showAlbumModal && selectedAlbumMedia && (
        <div 
          className="media-modal-overlay" 
          onClick={() => {
            setShowAlbumModal(false);
            setSelectedAlbumMedia(null);
          }}
          style={{ 
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
        >
          <div 
            className="media-modal-container" 
            onClick={(e) => e.stopPropagation()}
            style={{ 
              width: '95%',
              maxWidth: '1400px',
              maxHeight: '95vh', 
              overflow: 'auto',
              backgroundColor: '#fff',
              borderRadius: '16px',
              boxShadow: '0 12px 48px rgba(0, 0, 0, 0.3)',
              position: 'relative'
            }}
          >
            <button 
              className="media-modal-close" 
              onClick={() => {
                setShowAlbumModal(false);
                setSelectedAlbumMedia(null);
              }}
              style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                zIndex: 10001,
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
                e.currentTarget.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              &times;
            </button>
            
            {/* Album Header */}
            <div style={{ 
              padding: '30px 40px', 
              borderBottom: '2px solid #e4e6eb',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              borderTopLeftRadius: '16px',
              borderTopRightRadius: '16px'
            }}>
              <h2 style={{ 
                margin: '0 0 12px 0', 
                fontSize: '32px', 
                fontWeight: '700', 
                color: 'white',
                textShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}>
                {selectedAlbumMedia.event_name}
              </h2>
              {selectedAlbumMedia.description && (
                <p style={{ 
                  margin: '0 0 12px 0', 
                  fontSize: '16px', 
                  color: 'rgba(255, 255, 255, 0.95)',
                  lineHeight: '1.5'
                }}>
                  {selectedAlbumMedia.description}
                </p>
              )}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '20px',
                fontSize: '14px',
                color: 'rgba(255, 255, 255, 0.9)'
              }}>
                {selectedAlbumMedia.event_date && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '16px' }}>📅</span>
                    <span>{new Date(selectedAlbumMedia.event_date).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}</span>
                  </div>
                )}
                {selectedAlbumMedia.media && selectedAlbumMedia.media.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '16px' }}>📷</span>
                    <span>{selectedAlbumMedia.media.length} {selectedAlbumMedia.media.length === 1 ? 'item' : 'items'}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Album Media Grid */}
            <div style={{ padding: '30px 40px' }}>
              {selectedAlbumMedia.media && selectedAlbumMedia.media.length > 0 ? (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                  gap: '20px'
                }}>
                  {selectedAlbumMedia.media.map((media: SnippetMedia) => (
                    <div
                      key={media.id}
                      style={{
                        position: 'relative',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        backgroundColor: '#f0f2f5',
                        paddingTop: '100%',
                        cursor: 'pointer',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        transition: 'all 0.3s ease'
                      }}
                      onClick={() => {
                        if (media.media_type === 'image') {
                          window.open(snippetAlbumService.getFileUrl(media.file_path), '_blank');
                        }
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-4px)';
                        e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.2)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                      }}
                    >
                      {media.media_type === 'image' ? (
                        <>
                          <img
                            src={snippetAlbumService.getFileUrl(media.file_path)}
                            alt={media.caption || ''}
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'fill'
                            }}
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = "/assets/images/logo.png";
                            }}
                          />
                          <div style={{
                            position: 'absolute',
                            top: '10px',
                            right: '10px',
                            backgroundColor: 'rgba(0, 0, 0, 0.6)',
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: '600'
                          }}>
                            📷 Photo
                          </div>
                        </>
                      ) : (
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%'
                        }}>
                          <video
                            src={snippetAlbumService.getFileUrl(media.file_path)}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                            controls
                          />
                          <div style={{
                            position: 'absolute',
                            top: '10px',
                            right: '10px',
                            backgroundColor: 'rgba(0, 0, 0, 0.6)',
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: '600'
                          }}>
                            🎥 Video
                          </div>
                        </div>
                      )}
                      {media.caption && (
                        <div style={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          padding: '12px',
                          background: 'linear-gradient(to top, rgba(0, 0, 0, 0.8), transparent)',
                          color: 'white',
                          fontSize: '13px',
                          fontWeight: '500'
                        }}>
                          {media.caption}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ 
                  padding: '60px 20px', 
                  textAlign: 'center', 
                  color: '#65676b',
                  backgroundColor: '#f7f8fa',
                  borderRadius: '12px'
                }}>
                  <div style={{ fontSize: '64px', marginBottom: '16px' }}>📸</div>
                  <p style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 8px 0', color: '#050505' }}>
                    No photos or videos yet
                  </p>
                  <p style={{ fontSize: '14px', margin: 0 }}>
                    This album is empty. Add some photos or videos to get started!
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Dharma Modal */}
      {showDharmaModal && (
        <div className="dharma-tenets-modal-overlay" onClick={() => setShowDharmaModal(false)}>
          <div className="dharma-tenets-modal-container" onClick={(e) => e.stopPropagation()}>
            <button 
              className="dharma-tenets-modal-close" 
              onClick={() => setShowDharmaModal(false)}
              aria-label="Close"
            >
              &times;
            </button>
            
            <div className="dharma-tenets-modal-content">
              <div className="dharma-tenets-header">
                <h2>JWIL DHARMA</h2>
                <p className="dharma-tenets-subtitle">Principles guiding ethical conduct, accountability, and organizational excellence</p>
              </div>

              <div className="dharma-tenets-grid">
                <div className="dharma-tenet-card">
                  <div className="dharma-icon-wrapper dharma-emoji-icon">
                    🛡️
                  </div>
                  <h3>Integrity Breach</h3>
                  <p>Zero tolerance for dishonesty or ethical violations.</p>
                </div>

                <div className="dharma-tenet-card">
                  <div className="dharma-icon-wrapper dharma-emoji-icon">
                    ⚠️
                  </div>
                  <h3>Repeated Failures</h3>
                  <p>Consistent under-performance despite support and guidance.</p>
                </div>

                <div className="dharma-tenet-card">
                  <div className="dharma-icon-wrapper dharma-emoji-icon">
                    🚫
                  </div>
                  <h3>Rogue Behaviour</h3>
                  <p>Actions that go against company culture, values, or discipline.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tenets Modal */}
      {showTenetsModal && (
        <div className="dharma-tenets-modal-overlay" onClick={() => setShowTenetsModal(false)}>
          <div className="dharma-tenets-modal-container" onClick={(e) => e.stopPropagation()}>
            <button 
              className="dharma-tenets-modal-close" 
              onClick={() => setShowTenetsModal(false)}
              aria-label="Close"
            >
              &times;
            </button>
            
            <div className="dharma-tenets-modal-content">
              <div className="dharma-tenets-header">
                <h2>5 CULTURAL TENETS</h2>
                <p className="dharma-tenets-subtitle">Core beliefs guiding behavior, decision-making, and organizational culture</p>
              </div>

              <div className="dharma-tenets-grid tenets-grid-5">
                <div className="dharma-tenet-card tenet-card">
                  <div className="tenet-icon-wrapper">
                    💡
                  </div>
                  <h3>A Solution Mindset</h3>
                </div>

                <div className="dharma-tenet-card tenet-card">
                  <div className="tenet-icon-wrapper">
                    🤝
                  </div>
                  <h3>Empathy Does Not Mean Acceptance Of Nonperformance.</h3>
                </div>

                <div className="dharma-tenet-card tenet-card">
                  <div className="tenet-icon-wrapper">
                    ⚠️
                  </div>
                  <h3>Failure Is Not An Option.</h3>
                </div>

                <div className="dharma-tenet-card tenet-card">
                  <div className="tenet-icon-wrapper">
                    🧠
                  </div>
                  <h3>The Mindset Of A Leader, Manager And An Assignment Person.</h3>
                </div>

                <div className="dharma-tenet-card tenet-card">
                  <div className="tenet-icon-wrapper">
                    👑
                  </div>
                  <h3>Wealth Creation For All.</h3>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <CommonFooter />

      {/* Floating Chat Boat Icon */}
      <div className="floating-chat-boat">
        <button className="chat-boat-btn" aria-label="Chat Support" title="Chat with us">
          <img src="/assets/images/chat-boat.svg" alt="Chat Boat Icon" className="chat-boat-icon" />
        </button>
      </div>
      </main>
    </>
  );
};

export default HomePage;
